import React, { useState, useMemo } from 'react';
import { Card, CardContent, Grid, Tabs, Tab, Box, Divider } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

// Import new Tab Components
import BasicInfoTab from './BasicInfoTab';
import PricebooksTab from './PricebooksTab';
import CostbooksTab from './CostbooksTab';
import RateCardsTab from './RateCardsTab';
import ProductRulesTab from './ProductRulesTab';
import FulfillmentTab from './FulfillmentTab';
import DecompositionTab from './DecompositionTab'; // Import the new DecompositionTab
import VersionHistoryTab from './VersionHistoryTab';
import { grey } from '@mui/material/colors';

import { useVisibility } from 'context/visibilityContext';
import PriceAdjustersTab from './PriceAdjustersTab';

function ProductDetailsView({ org, product, allProducts, onClose }) {
  const [tabValue, setTabValue] = useState(0);

  const { visibilityConfig } = useVisibility();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const {
    productRuleBasicFields,
    cpqDiscountScheduleFields,
    rcaPriceAdjScheduleFields,
    consumptionScheduleFields,
    fulfillmentFieldsConfig = {},
    decompositionFieldsConfig = {}, // Add config for the new tab
  } = useMemo(() => {
    const findFields = (objectName) => {
      const configSource = org?.type?.toLowerCase() === 'rca' ? visibilityConfig.rca : visibilityConfig.cpq;
      const config = Array.isArray(configSource)
        ? configSource.find((obj) => obj.objectName === objectName)
        : undefined;
      return config?.displayColumns ? config.displayColumns.split(',').map((s) => s.trim()) : [];
    };

    return {
      productRuleBasicFields: findFields('SBQQ__ProductRule__c'),
      cpqDiscountScheduleFields: findFields('SBQQ__DiscountSchedule__c'),
      rcaPriceAdjScheduleFields: findFields('PriceAdjustmentSchedule'),
      consumptionScheduleFields: findFields('ConsumptionSchedule'),
      fulfillmentFieldsConfig: {
        assignmentRuleFields: findFields('FulfillmentTaskAssignmentRule'),
        scenarioFields: findFields('ProductFulfillmentScenario'),
        stepFields: findFields('FulfillmentStepDefinition'),
        falloutRuleFields: findFields('FulfillmentFalloutRule'),
        jeopardyRuleFields: findFields('FulfillmentJeopardyRule'),
      },
      // Define the config object for the Decomposition tab
      decompositionFieldsConfig: {
        decompRuleFields: findFields('ProductFulfillmentDecompRule'),
        valueTransformationFields: findFields('ValueTransformation'),
      },
    };
  }, [visibilityConfig, org]);

  const renderFields = (fields) => {
    if (!fields) return <ArgonTypography variant="caption">No details available.</ArgonTypography>;
    return (
      <ArgonBox>
        {Object.entries(fields).map(([key, value]) => (
          <ArgonBox key={key} mb={1} width="100%">
            <Grid container alignItems="center" spacing={0}>
              <Grid item xs={7}>
                <ArgonTypography
                  variant="caption"
                  fontWeight="bold"
                  color="textSecondary"
                  align="left"
                  sx={{ wordWrap: 'break-word', color: grey[700] }}
                  ml={3}
                >
                  {key}
                </ArgonTypography>
              </Grid>
              <Grid item xs={1} style={{ textAlign: 'center' }}>
                <Divider orientation="vertical" flexItem />
              </Grid>
              <Grid item xs={3} style={{ textAlign: 'center' }}>
                <ArgonBox
                  bgcolor={grey[200]}
                  color={grey[700]}
                  p={1}
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: '100%', boxSizing: 'border-box' }}
                >
                  <ArgonTypography variant="caption" fontWeight="bold" sx={{ wordWrap: 'break-word', width: '100%' }}>
                    {String(value) || '-'}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>
            </Grid>
          </ArgonBox>
        ))}
      </ArgonBox>
    );
  };

  if (!product) {
    return <ArgonBox p={3}>No product selected.</ArgonBox>;
  }

  const isRCA = org?.type === 'RC';

  return (
    <Card>
      <CardContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="product details tabs">
            <Tab label="Basic Info" {...a11yProps(0)} />
            <Tab label="Pricebooks" {...a11yProps(1)} />
            <Tab label="Costbooks" {...a11yProps(2)} />
            <Tab label="Rate Cards" {...a11yProps(3)} />
            <Tab label="Price Adjusters" {...a11yProps(4)} />
            <Tab label="Product Rules" {...a11yProps(5)} />
            {/* --- TAB ORDER UPDATED --- */}
            {isRCA && <Tab label="Fulfillment" {...a11yProps(6)} />}
            {isRCA && <Tab label="Decomposition" {...a11yProps(7)} />}
            <Tab label="Version History" {...a11yProps(isRCA ? 8 : 6)} />
          </Tabs>
        </Box>
        <TabPanel value={tabValue} index={0}>
          <BasicInfoTab product={product} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <PricebooksTab product={product} renderFieldsProp={renderFields} />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <CostbooksTab product={product} renderFieldsProp={renderFields} />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <RateCardsTab product={product} renderFieldsProp={renderFields} basicFields={consumptionScheduleFields} />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <PriceAdjustersTab
            product={product}
            org={org}
            renderFieldsProp={renderFields}
            cpqBasicFields={cpqDiscountScheduleFields}
            rcaBasicFields={rcaPriceAdjScheduleFields}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={5}>
          <ProductRulesTab product={product} basicFields={productRuleBasicFields} renderFieldsProp={renderFields} />
        </TabPanel>
        {/* --- TABPANEL ORDER UPDATED --- */}
        {isRCA && (
          <TabPanel value={tabValue} index={6}>
            <FulfillmentTab product={product} fieldsConfig={fulfillmentFieldsConfig} />
          </TabPanel>
        )}
        {isRCA && (
          <TabPanel value={tabValue} index={7}>
            <DecompositionTab product={product} fieldsConfig={decompositionFieldsConfig} />
          </TabPanel>
        )}
        <TabPanel value={tabValue} index={isRCA ? 8 : 6}>
          <VersionHistoryTab product={product} allProducts={allProducts} />
        </TabPanel>
      </CardContent>
    </Card>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-details-tabpanel-${index}`}
      aria-labelledby={`product-details-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `product-details-tab-${index}`,
    'aria-controls': `product-details-tabpanel-${index}`,
  };
}

export default ProductDetailsView;
